import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, QrCode, Keyboard, CheckCircle, AlertTriangle, Loader2, UserCheck, Gift,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import QrScanner from '@/components/QrScanner';
import api from '@/lib/api';

// Door check-in page for hosts (admins + event co-admins).
export default function CheckInPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [event, setEvent] = useState(null);
  const [reg, setReg] = useState(null);
  const [manualCode, setManualCode] = useState('');
  const [checking, setChecking] = useState(false);
  const [lastResult, setLastResult] = useState(null); // {status, name, tier}
  const [markingId, setMarkingId] = useState(null);

  const canHost = user?.is_admin || user?.can_manage_events;

  const load = useCallback(async () => {
    try {
      const [evRes, regRes] = await Promise.all([
        api.get(`/events/${eventId}`),
        api.get(`/events/${eventId}/registrations`),
      ]);
      setEvent(evRes.data);
      setReg(regRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load event');
    }
  }, [eventId]);

  useEffect(() => { if (canHost) load(); }, [load, canHost]);

  const doCheckin = async (code) => {
    if (!code?.trim()) return;
    setChecking(true);
    setLastResult(null);
    try {
      const res = await api.post(`/events/${eventId}/checkin`, { ticket_code: code.trim() });
      setLastResult(res.data);
      if (res.data.status === 'checked_in') {
        toast.success(`${res.data.name} checked in!`);
      } else {
        toast.info(`${res.data.name} was already checked in`);
      }
      setManualCode('');
      await load();
    } catch (e) {
      setLastResult({ status: 'error', message: e.response?.data?.detail || 'Check-in failed' });
      toast.error(e.response?.data?.detail || 'Check-in failed');
    }
    setChecking(false);
  };

  const tapCheckin = async (rsvpId, attended) => {
    setMarkingId(rsvpId);
    try {
      await api.post(`/events/${eventId}/attendance`, { rsvp_id: rsvpId, attended });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update');
    }
    setMarkingId(null);
  };

  if (!canHost) {
    return (
      <div className="min-h-screen bg-warm-white flex items-center justify-center font-body px-6">
        <div className="text-center">
          <AlertTriangle size={32} className="text-amber-500 mx-auto mb-3" />
          <p className="text-navy font-semibold">Check-in is for event hosts only.</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4 rounded-full bg-teal hover:bg-teal-deep">Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  const confirmed = (reg?.registrations || []).filter((r) => r.status === 'confirmed');
  const attendedCount = confirmed.filter((r) => r.attended === true).length;

  return (
    <div className="min-h-screen bg-warm-white font-body pb-16" data-testid="checkin-page">
      <div className="max-w-2xl mx-auto px-4 pt-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-teal hover:text-teal-deep mb-4"
          data-testid="checkin-back-btn"
        >
          <ArrowLeft size={18} /> Back
        </button>

        <div className="bg-navy text-white rounded-2xl p-5 mb-5">
          <p className="text-xs uppercase tracking-wide text-white/60 flex items-center gap-1.5">
            <QrCode size={13} /> Door Check-In
          </p>
          <h1 className="font-display text-2xl mt-1">{event?.title || '…'}</h1>
          <p className="text-sm text-white/80 mt-1" data-testid="checkin-progress">
            {attendedCount} of {confirmed.length} checked in
          </p>
        </div>

        {/* Scanner */}
        <div className="bg-white rounded-2xl shadow-card p-5 mb-5" data-testid="checkin-scanner-card">
          <QrScanner onScan={(text) => doCheckin(text)} onError={() => {}} />
          <div className="flex items-center gap-2 mt-4">
            <Keyboard size={16} className="text-text-mid shrink-0" />
            <Input
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value.toUpperCase())}
              placeholder="Or type ticket code: CMC-XXXXXXXX"
              className="rounded-xl font-mono"
              onKeyDown={(e) => e.key === 'Enter' && doCheckin(manualCode)}
              data-testid="manual-ticket-input"
            />
            <Button
              onClick={() => doCheckin(manualCode)}
              disabled={checking || !manualCode.trim()}
              className="rounded-full bg-teal hover:bg-teal-deep"
              data-testid="manual-checkin-btn"
            >
              {checking ? <Loader2 size={16} className="animate-spin" /> : 'Check In'}
            </Button>
          </div>

          {lastResult && (
            <div
              className={`mt-4 rounded-xl p-4 flex items-center gap-3 ${
                lastResult.status === 'checked_in' ? 'bg-green-50 border border-green-200'
                : lastResult.status === 'already_checked_in' ? 'bg-amber-50 border border-amber-200'
                : 'bg-red-50 border border-red-200'
              }`}
              data-testid="checkin-result"
            >
              {lastResult.status === 'error' ? (
                <>
                  <AlertTriangle size={20} className="text-red-500" />
                  <p className="text-sm text-red-700">{lastResult.message}</p>
                </>
              ) : (
                <>
                  <CheckCircle size={20} className={lastResult.status === 'checked_in' ? 'text-green-600' : 'text-amber-600'} />
                  <div>
                    <p className="font-semibold text-navy">{lastResult.name}</p>
                    <p className="text-xs text-text-mid">
                      {lastResult.status === 'checked_in' ? 'Checked in' : 'Already checked in'} · {lastResult.tier}
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Manual list fallback */}
        <div className="bg-white rounded-2xl shadow-card p-5" data-testid="checkin-list-card">
          <h3 className="font-semibold text-navy mb-3 flex items-center gap-2">
            <UserCheck size={16} className="text-teal" /> Registrants — tap to check in
          </h3>
          {confirmed.length === 0 ? (
            <p className="text-sm text-text-mid py-4 text-center">No confirmed registrations</p>
          ) : (
            <div className="divide-y divide-border-subtle">
              {confirmed.map((r) => (
                <div key={r.rsvp_id} className="flex items-center justify-between py-3 gap-3" data-testid={`checkin-row-${r.rsvp_id}`}>
                  <div className="min-w-0">
                    <p className="font-medium text-navy text-sm flex items-center gap-2">
                      {r.name}
                      {r.is_guest_pass && (
                        <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 text-[10px] font-bold px-2 py-0.5 rounded-full">
                          <Gift size={10} /> GUEST
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-text-mid truncate">{r.email}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="outline" className={r.tier === 'thrive' ? 'bg-teal/10 text-teal' : ''}>{r.tier}</Badge>
                    <Button
                      size="sm"
                      onClick={() => tapCheckin(r.rsvp_id, !(r.attended === true))}
                      disabled={markingId === r.rsvp_id}
                      className={`rounded-full text-xs ${r.attended === true ? 'bg-green-600 hover:bg-green-700' : 'bg-navy hover:bg-navy/90'} text-white`}
                      data-testid={`tap-checkin-${r.rsvp_id}`}
                    >
                      {markingId === r.rsvp_id ? <Loader2 size={13} className="animate-spin" /> : r.attended === true ? '✓ Here' : 'Check in'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
